import {
  ChangeEvent,
  useState,
  useEffect,
  useCallback,
  createRef,
  ReactElement,
} from "react";
import jaroWinklerDistance from "jaro-winkler";
import { useDispatch, useSelector } from "react-redux";
import { CountryFieldConfig } from "@evergiving/common/dist/es2015/form";

import { getFlag } from "@/lib/dynamic/countryFlagsForSelector";
import { RootState } from "@/lib/redux/types";
import { setCountryCode } from "@/lib/redux/actions";
import { CountryButton } from "./CountryButton";
import { CountryFlag } from "./CountryFlag";

export function CountryField({
  countryFieldConfig,
}: {
  countryFieldConfig: CountryFieldConfig;
}): ReactElement {
  const currentCountryCode = useSelector(
    (state: RootState) => state.countryCode.value
  );

  const [text, setText] = useState<string>(
    countryFieldConfig.supportedCountryNameTexts[currentCountryCode || "ZZ"]
  );
  const [matching, setMatching] = useState<string[]>(
    currentCountryCode ? [currentCountryCode] : []
  );

  const [open, setOpen] = useState<boolean>(false);

  const [selectedIndex, setSelectedIndex] = useState<number>(-1);

  const updateMatching = useCallback(
    (newMatching: string[]) => {
      const prevSelected = selectedIndex >= 0 ? matching[selectedIndex] : "";
      if (JSON.stringify(newMatching) !== JSON.stringify(matching)) {
        setMatching(newMatching);
        setSelectedIndex(newMatching.indexOf(prevSelected));
      }
    },
    [matching, selectedIndex]
  );

  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    if (event.target) {
      setText(event.target.value);
      setTimeout(() => {
        setOpen(true);
      }, 50);
    }
  }, []);

  useEffect(() => {
    const textNoWhitespace = (text ?? "")
      .replace(/^\s+/, "")
      .replace(/\s+$/, "");
    if (textNoWhitespace.length === 0) {
      updateMatching(currentCountryCode ? [currentCountryCode] : []);
    } else {
      const newMatching = Object.entries(
        countryFieldConfig.supportedCountryNameTexts
      )
        .map(([countryCode, countryName]) => ({
          countryCode,
          distance: jaroWinklerDistance(textNoWhitespace, countryName, {
            caseSensitive: false,
          }),
        }))
        .filter(({ distance }) => distance >= 0.75);

      newMatching.sort((a, b) => b.distance - a.distance);
      updateMatching(newMatching.map(({ countryCode }) => countryCode));
    }
  }, [text, countryFieldConfig, currentCountryCode, updateMatching]);

  const inputEl = createRef<HTMLInputElement>();

  const handleFocus = useCallback(() => {
    if (inputEl.current) {
      inputEl.current.select();
    }
  }, [inputEl]);

  const dispatch = useDispatch();

  const handleCountryClick = useCallback(
    (countryCode) => {
      setOpen(false);
      setText(countryFieldConfig.supportedCountryNameTexts[countryCode]);
      dispatch(setCountryCode(countryCode));
    },
    [dispatch, countryFieldConfig.supportedCountryNameTexts]
  );

  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") {
        event.preventDefault();
        if (matching.length > 0) {
          const countryCode = matching[Math.max(selectedIndex, 0)];
          setOpen(false);
          setText(countryFieldConfig.supportedCountryNameTexts[countryCode]);
          dispatch(setCountryCode(countryCode));
          const inputElCurrent = inputEl.current;
          if (inputElCurrent) {
            setTimeout(() => {
              inputElCurrent.select();
            }, 0);
          }
        }
      }
    },
    [
      dispatch,
      inputEl,
      countryFieldConfig.supportedCountryNameTexts,
      matching,
      selectedIndex,
    ]
  );

  const moveSelection = useCallback(
    (delta: number) => {
      setSelectedIndex(
        (selectedIndex + matching.length + delta) % matching.length
      );
    },
    [selectedIndex, matching.length]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "ArrowDown" && open) {
        moveSelection(1);
      } else if (event.key === "ArrowUp" && open) {
        moveSelection(-1);
      } else if (event.key === "Escape") {
        setSelectedIndex(-1);
        setOpen(false);
        setText(
          currentCountryCode
            ? countryFieldConfig.supportedCountryNameTexts[currentCountryCode]
            : ""
        );
        const inputElCurrent = inputEl.current;
        if (inputElCurrent) {
          setTimeout(() => {
            inputElCurrent.select();
          }, 0);
        }
      }
    },
    [
      moveSelection,
      inputEl,
      currentCountryCode,
      open,
      countryFieldConfig.supportedCountryNameTexts,
    ]
  );

  useEffect(() => {
    setText(
      currentCountryCode
        ? countryFieldConfig.supportedCountryNameTexts[currentCountryCode]
        : ""
    );
  }, [currentCountryCode, countryFieldConfig.supportedCountryNameTexts]);

  const selectedForFlag =
    matching[Math.max(0, selectedIndex)] || currentCountryCode;

  return (
    <div
      className="countryComboBox"
      role="combobox"
      aria-haspopup="listbox"
      aria-expanded={open}
      aria-owns="country-list"
      aria-controls="country-list"
    >
      <div className="textField countryField">
        <div className="flagContainer">
          {selectedForFlag && (
            <CountryFlag countryCode={selectedForFlag} getFlag={getFlag} />
          )}
        </div>
        <input
          id="countryInput"
          type="text"
          placeholder={countryFieldConfig.placeholderText}
          value={text}
          onChange={handleChange}
          onFocus={handleFocus}
          onKeyPress={handleKeyPress}
          onKeyDown={handleKeyDown}
          ref={inputEl}
          aria-autocomplete="list"
          aria-activedescendant={
            selectedIndex >= 0
              ? `country-option-${matching[selectedIndex]}`
              : undefined
          }
        />
      </div>
      {open && (
        <ul role="listbox" id="country-list">
          {matching.map((countryCode, index) => (
            <CountryButton
              key={countryCode}
              countryCode={countryCode}
              onClick={handleCountryClick}
              selected={index === selectedIndex}
              supportedCountryNameTexts={
                countryFieldConfig.supportedCountryNameTexts
              }
            />
          ))}
        </ul>
      )}
    </div>
  );
}
